/**
 * Handles API call to send outbound messages. Returns Promise<boolean> so modal
 * knows whether to close (success) or stay open for retry (failure).
 *
 * API: POST /api/desks/:desk_id/conversations/outbound (matches backend PR #1650)
 */

import { useCallback } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { conversationsEndpoints } from '@/api/endpoints';
import { callPost } from '@/api/fetcher';
import { useDeskId } from '@/hooks/useDeskId';
import { formatComponentsFromForm } from '@/modules/whatsapp/utils';
import { toErrorReason } from '@/segment/errorReason';
import { trackEvent } from '@/segment/segment';
import { WhatsappTemplate } from '@/shared/types/broadcast';
import { Integration, IntegrationType } from '@/shared/types/integration';
import { EventName } from '@/shared/types/segment';

import { ContactLookupResult } from './useContactLookup';
import { conversationKeys } from './useConversations';
import { NewMessageFormValues } from './useNewMessageForm';

// Payload types for the outbound message API
interface WhatsAppTemplatePayload {
  name: string;
  language: string;
  components: ReturnType<typeof formatComponentsFromForm>;
  id?: string;
  category?: string;
}

interface TextTemplatePayload {
  text: string;
}

interface EmailTemplatePayload {
  subject: string;
  body: string;
}

interface SendOutboundMessagePayload {
  external_id: string;
  integration_id: string;
  channel: IntegrationType;
  template: WhatsAppTemplatePayload | TextTemplatePayload | EmailTemplatePayload;
  brain_id?: string | null;
  context?: Record<string, unknown>;
}

/** Posts outbound message to the backend API. */
const sendOutboundMessage = (deskId: string, payload: SendOutboundMessagePayload) =>
  callPost(conversationsEndpoints.sendOutboundMessage(deskId), payload);

/** Maps known backend error codes to user-friendly i18n translation keys. */
const ERROR_CODE_TRANSLATION_KEYS: Record<string, string> = {
  'send-outbound-conversation': 'conversation.new_message.error_duplicate_outbound',
};

/** Maps API errors to user-friendly i18n translation keys. */
const getErrorTranslationKey = (error: Error & { code?: string }): string => {
  if (error.code && ERROR_CODE_TRANSLATION_KEYS[error.code]) {
    return ERROR_CODE_TRANSLATION_KEYS[error.code];
  }

  const message = (error.message || '').toLowerCase();

  if (message.includes('network') || message.includes('fetch') || message.includes('connection')) {
    return 'conversation.new_message.error_network';
  }

  if (message.includes('rate limit') || message.includes('too many requests')) {
    return 'conversation.new_message.error_rate_limit';
  }

  if (message.includes('unauthorized') || message.includes('forbidden')) {
    return 'conversation.new_message.error_unauthorized';
  }

  if (message.includes('invalid') && (message.includes('phone') || message.includes('recipient'))) {
    return 'conversation.new_message.validation.invalid_phone';
  }

  return 'conversation.new_message.error_generic';
};

interface UseNewMessageSendProps {
  form: UseFormReturn<NewMessageFormValues>;
  selectedIntegration: Integration | null;
  selectedIntegrationType: IntegrationType | null;
  selectedTemplate: WhatsappTemplate | null;
  contactContext?: ContactLookupResult | null;
}

/** Handles sending outbound messages. Returns true on success, false on failure. */
export const useNewMessageSend = ({
  form,
  selectedIntegration,
  selectedIntegrationType,
  selectedTemplate,
  contactContext,
}: UseNewMessageSendProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const deskId = useDeskId();
  const { trigger, getValues } = form;

  const { mutateAsync: sendMessageAsync, isPending: isSending } = useMutation({
    mutationFn: (payload: SendOutboundMessagePayload) => {
      if (!deskId) {
        return Promise.reject(new Error('No desk ID available'));
      }
      return sendOutboundMessage(deskId, payload);
    },
    onSuccess: () => {
      trackEvent(EventName.NewMessageSendSuccess, {
        integration_type: selectedIntegrationType ?? undefined,
        is_new_contact: !contactContext,
        has_ai_agent: !!form.getValues('aiAgent'),
      });
      // Success toast with conversation link is handled by useRtmOutboundProcessed
      // once delivery is confirmed asynchronously.
      queryClient.invalidateQueries({ queryKey: conversationKeys.base(deskId) });
    },
    onError: (error: Error & { code?: string }) => {
      trackEvent(EventName.NewMessageSendError, {
        integration_type: selectedIntegrationType ?? undefined,
        error_reason: toErrorReason(error),
        is_new_contact: !contactContext,
        has_ai_agent: !!form.getValues('aiAgent'),
      });
      const translationKey = getErrorTranslationKey(error);
      toast.error(t(translationKey));
    },
  });

  const sendMessage = useCallback(async (): Promise<boolean> => {
    if (!deskId || !selectedIntegration || !selectedIntegrationType) {
      return false;
    }

    const isValid = await trigger();
    if (!isValid) {
      return false;
    }

    const formData = getValues();

    const externalId =
      selectedIntegrationType === IntegrationType.EMAIL ? formData.toEmail : formData.toPhone;

    // Build channel-specific template payload
    let template: WhatsAppTemplatePayload | TextTemplatePayload | EmailTemplatePayload;
    if (selectedIntegrationType === IntegrationType.WHATSAPP && selectedTemplate) {
      template = {
        name: selectedTemplate.name,
        language: selectedTemplate.language,
        components: formatComponentsFromForm(
          selectedTemplate,
          formData as Parameters<typeof formatComponentsFromForm>[1]
        ),
      };
      if (selectedTemplate.id) {
        template.id = selectedTemplate.id;
      }
      if (selectedTemplate.category) {
        template.category = selectedTemplate.category;
      }
    } else if (selectedIntegrationType === IntegrationType.EMAIL) {
      template = { subject: formData.subject || '', body: formData.content || '' };
    } else {
      template = { text: formData.content || '' };
    }

    const payload: SendOutboundMessagePayload = {
      external_id: externalId,
      integration_id: selectedIntegration.integration_id,
      channel: selectedIntegrationType,
      template,
    };

    if (formData.aiAgent) {
      payload.brain_id = formData.aiAgent.brain_id ?? formData.aiAgent.brain_parent_id;
    }

    if (contactContext) {
      payload.context = {
        user: { display_name: contactContext.userName },
        previous_sessions: contactContext.sessionCount,
        last_session_id: contactContext.lastSessionId,
      };
    } else if (formData.recipientName?.trim()) {
      payload.context = { user: { display_name: formData.recipientName.trim() } };
    }

    trackEvent(EventName.ClickNewMessageSend, {
      integration_type: selectedIntegrationType,
      has_template: !!selectedTemplate,
      is_new_contact: !contactContext,
      has_ai_agent: !!formData.aiAgent,
    });

    try {
      await sendMessageAsync(payload);
      return true;
    } catch {
      return false;
    }
  }, [
    deskId,
    trigger,
    getValues,
    selectedIntegration,
    selectedIntegrationType,
    selectedTemplate,
    sendMessageAsync,
    contactContext,
  ]);

  return { sendMessage, isSending };
};
